'use client'

import 'src/hooks/howlerConfig'
import { Howler } from 'howler'
import { useEffect, useRef } from 'react'
import { toSafeVolume } from 'src/utils/volumeUtils'
import { createHowl } from './createHowl'
import { isBirdieEffect, playBirdieEffect, setBirdieEffectsLevel, stopBirdieEffects } from './birdieEffects'
import {
  CRASH_COUNTDOWN_BUILD_MS,
  CRASH_COUNTDOWN_PEAK_MS,
  CRASH_COUNTDOWN_PEAK_START_MS
} from './crashCountdownSprite'
import {
  useGetCurrentEffects,
  useGetIsCrashCountdownPlaying,
  useGetCrashCountdownGain,
  useGetEffectsVolume,
  useGetIsEffectsMuted,
  useGetIsMasterMuted,
  useGetMasterVolume,
  useSoundActions,
  useSoundStore
} from 'src/store/sound'

// A phase-driven effect (crashBust, lightningRound) queued longer than this is
// stale — the tab was backgrounded/suspended and the moment has passed.
const STALE_EFFECT_MS = 1500

// Module scope means these construct during chunk evaluation, under React
// hydration — createHowl (not bare `new Howl`) so an audio-init throw can
// never reject the chunk import and blank the page (seen on Safari).
const soundEffects = createHowl({
  autoplay: false,
  preload: true,
  src: ['/assets/sounds/effects/effects.mp3'],
  volume: 1,
  sprite: {
    cancel: [0, 391.83673469387753],
    chip: [2000, 290.4761904761903],
    click: [4000, 208.97959183673453],
    marbleSelected: [6000, 432.01814058956955],
    marbleSelectedReverse: [6000, 432.01814058956955],
    panelReveal: [8000, 888.0045351473917],
    panelRevealReverse: [8000, 888.0045351473917],
    reward: [10000, 1988.2993197278918],
    betPlaced: [13000, 679.183673469387],
    switch: [15000, 696.0090702947853],
    bonus: [17031.83, 2112]
  }
})

// Lightning round sound effect (separate file, reduced volume)
const lightningRoundSound = createHowl({
  autoplay: false,
  preload: true,
  src: ['/assets/sounds/effects/lightning_round.mp3', '/assets/sounds/effects/lightning_round.aac'],
  volume: 0.45
})

// Crash bust "thump" — the loss counterpart to the per-zone reward cue, fired
// once at the panel when the round crashes with the player still riding
// (CrashChipPanel). Standalone file (a ~3s massive thump), NOT part of the
// effects sprite. Full effects volume for now; TUNABLE — the owner will judge
// the loudness by ear and this constant can drop (e.g. * 0.6) if it's too hot.
const crashBustSound = createHowl({
  autoplay: false,
  preload: true,
  src: ['/assets/sounds/effects/crash_bust.mp3', '/assets/sounds/effects/crash_bust.aac'],
  volume: 1
})

// ── Crash rising-tension cue ───────────────────────────────────────────────
// A 16.6s track that has to cover a round of UNKNOWN length: 2x lands ~18s in,
// and a round can run another ~60s beyond that. Its own anatomy solves it —
// discrete ticks accelerate until they merge into a continuous plateau at
// ~10-13.5s, then a tail decays away.
//
//   build : the whole acceleration, played once
//   peak  : the 3.5s continuous plateau, LOOPED for as long as the round lives
//
// Looping the WHOLE track would snap back to slow ticks exactly when tension
// should be highest. Looping only the plateau means the cue builds, peaks and
// HOLDS at the peak — which is what an ever-climbing multiplier deserves.
//
// The tail (13.5s+) is deliberately unused: the bust thump is the payoff.
// Boundaries live in ./crashCountdownSprite — the gain curve derives its swell
// from the build length, so the two must read the same numbers.
const crashCountdownSound = createHowl({
  autoplay: false,
  preload: true,
  // mp3 ONLY, deliberately. The .aac twin decodes 23ms later (AAC encoder
  // delay), so the sprite boundaries — which are sample-accurate zero crossings
  // measured on the mp3 — would land mid-cycle there and tick on every loop.
  // The main effects sprite sheet above is mp3-only for the same reason.
  src: ['/assets/sounds/effects/countdown.mp3'],
  sprite: {
    build: [0, CRASH_COUNTDOWN_BUILD_MS],
    peak: [CRASH_COUNTDOWN_PEAK_START_MS, CRASH_COUNTDOWN_PEAK_MS, true]
  },
  // Ceiling, not a constant: the live gain SWELLS toward this with the
  // multiplier (see crashCountdownGain). 0.4 keeps the sustained loop sitting
  // under the round rather than dominating it — it can run for a minute.
  volume: 0.4
})

export function SoundEffects() {
  const soundStoreActions = useSoundActions()
  // Volume state
  const masterVolume = useGetMasterVolume()
  const effectsVolume = useGetEffectsVolume()
  const currentSoundEffects = useGetCurrentEffects()
  const isCrashCountdownPlaying = useGetIsCrashCountdownPlaying()
  const crashCountdownGain = useGetCrashCountdownGain()
  const volume = toSafeVolume(effectsVolume * masterVolume, 0)

  // Muted state
  const isMasterMuted = useGetIsMasterMuted()
  const isEffectsMuted = useGetIsEffectsMuted()
  const muted = isEffectsMuted || isMasterMuted

  // Play sound effects. Phase-driven cues (crashBust, lightningRound) are
  // time-sensitive: don't play while the tab is hidden (a play() onto a
  // suspended AudioContext replays on resume) or if the cue has sat in the
  // queue too long (backgrounded/suspended — the moment passed). Always clear
  // so nothing lingers. UI cues (click/chip/...) fire fresh from a visible tab,
  // so the guard never drops them. Cues are id'd with Date.now() (sound store).
  useEffect(() => {
    if (currentSoundEffects.length === 0) return
    const hidden = typeof document !== 'undefined' && document.hidden
    const now = Date.now()
    currentSoundEffects.forEach((effect) => {
      const ageMs = now - Number(effect.id)
      const stale = !Number.isNaN(ageMs) && ageMs > STALE_EFFECT_MS
      if (!hidden && !stale) {
        if (isBirdieEffect(effect.type)) {
          playBirdieEffect(effect.type)
        } else if (effect.type === 'lightningRound') {
          lightningRoundSound.play()
        } else if (effect.type === 'crashBust') {
          crashBustSound.play()
        } else {
          soundEffects.play(effect.type)
        }
      }
      soundStoreActions.clearEffect(effect.id)
    })
  }, [currentSoundEffects, soundStoreActions])

  // ── Crash tension cue: build once, then hold on the looped peak ──────────
  // Kept in a ref rather than state so the handoff never re-renders. The store
  // flag is the single source of truth: it goes false on crash, on round end,
  // on leaving the game and on tab-hide, and each of those stops the sound.
  const countdownIdsRef = useRef<{ build?: number; peak?: number }>({})
  // True while a stop fade is in flight. The volume effect below must not write
  // the GROUP volume during that window — howler's volume() calls _stopFade()
  // internally, so the write would CANCEL the fade and leave the stop() that
  // follows to hard-cut a full-amplitude loop.
  const countdownStoppingRef = useRef(false)
  // Bumped on every start and every stop, so a deferred teardown can tell
  // whether the cue it was about to silence is still the current one.
  const countdownGenRef = useRef(0)
  // Latest resolved cue volume, mirrored so the start path can apply it WITHOUT
  // taking volume/mute/gain as effect deps (which would restart the sound on
  // every swell tick).
  const countdownVolumeRef = useRef(0)

  useEffect(() => {
    const ids = countdownIdsRef.current

    const stopAll = () => {
      const gen = ++countdownGenRef.current
      const live = [ids.build, ids.peak].filter((id): id is number => id !== undefined)
      countdownIdsRef.current = {}

      // Short fade instead of a hard stop: cutting a loud continuous loop dead
      // clicks, and the bust thump lands better arriving into a quick decay
      // than into a hard edge.
      if (live.length > 0) {
        countdownStoppingRef.current = true
        live.forEach((id) => {
          crashCountdownSound.fade(crashCountdownSound.volume(id) as number, 0, 90, id)
        })
      }

      window.setTimeout(() => {
        // A new round already started the cue again — leave it alone.
        if (countdownGenRef.current !== gen) return
        countdownStoppingRef.current = false
        // Bare stop(), no id: stops EVERY sound in the group. Tracked ids are
        // not enough — howler can defer a play() behind _playLock and hand back
        // an id for a sound that has not started yet, and the build's id is
        // recycled the moment it ends. This is the only call that reaches those.
        crashCountdownSound.stop()
      }, 100)
    }

    if (!isCrashCountdownPlaying) {
      stopAll()
      return undefined
    }

    // Never start onto a context that is not actually RUNNING. howler defers
    // such a play() behind an uncancellable once('resume') and still returns an
    // id, so stopAll() has nothing it can stop and the deferred sound — the
    // LOOPING peak, once the build has handed over — fires whenever the context
    // resumes, out of round. document.hidden does not cover this on its own: a
    // visible tab that has never been clicked has a suspended context, and an
    // interruption (a call, Siri) suspends it while the tab is still visible.
    if (typeof document !== 'undefined' && document.hidden) return undefined
    // `Howler.state` is real at runtime but missing from @types/howler. It is
    // the flag howler itself branches on in play(), so checking ctx.state alone
    // would not match its decision.
    const howlerState = (Howler as unknown as { state?: string }).state
    if (Howler.ctx?.state !== 'running' || howlerState !== 'running') return undefined

    countdownGenRef.current += 1
    countdownStoppingRef.current = false
    // Apply the current swell level BEFORE the first sample sounds, so the cue
    // never enters at the previous round's level for a frame.
    crashCountdownSound.volume(countdownVolumeRef.current)

    const buildId = crashCountdownSound.play('build')
    ids.build = buildId

    // When the acceleration finishes, hand off to the looped plateau so the
    // tension HOLDS rather than resetting to slow ticks.
    const onBuildEnd = (endedId: number) => {
      if (endedId !== buildId) return
      if (!useSoundStore.getState().isCrashCountdownPlaying) return
      countdownIdsRef.current.peak = crashCountdownSound.play('peak')
    }
    crashCountdownSound.on('end', onBuildEnd)

    return () => {
      crashCountdownSound.off('end', onBuildEnd)
      stopAll()
    }
  }, [isCrashCountdownPlaying])

  // Flush on hide (see SoundNotifications): stop scheduled effect audio and
  // drop the queue the instant the tab hides, so a stale crash-bust thump or
  // lightning cue can't replay on refocus.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.hidden) {
        soundStoreActions.clearAllEffects()
        soundEffects.stop()
        lightningRoundSound.stop()
        stopBirdieEffects()
        crashBustSound.stop()
        crashCountdownSound.stop()
        soundStoreActions.setCrashCountdown(false)
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [soundStoreActions])

  // Set volume
  useEffect(() => {
    soundEffects.volume(muted ? 0 : volume)
    // Lightning sound at 45% of normal volume
    lightningRoundSound.volume(muted ? 0 : volume * 0.45)
    // Birdie's cues carry their own mix under the channel (birdieEffects.ts).
    setBirdieEffectsLevel(volume, muted)
    // Crash bust thump at full effects volume (TUNABLE — drop the factor if
    // the ~3s thump is too loud once the owner judges it by ear).
    crashBustSound.volume(muted ? 0 : volume)
    // Tension cue sits UNDER the bust thump — it plays for up to a minute, so
    // it must not fatigue. TUNABLE by ear.
    // Applied to the GROUP rather than per-sound-id so a mute or volume change
    // lands mid-swell too — but that is also why it
    // must stand down while a stop fade is running: howler's volume() calls
    // _stopFade() internally, so writing here would cancel the 90ms fade-out and
    // leave the stop() behind it to hard-cut the loop at full amplitude, right
    // under the bust thump. The crash flips the playing flag and the gain in the
    // same commit, so without this guard that collision happened every round.
    // crashCountdownGain already carries the device profile's ceiling (see
    // crashCountdownGain.ts) — the level differs on mobile, where the desktop
    // tuning is inaudible through a phone speaker. Applying a second constant
    // factor here is exactly how the two would drift apart.
    countdownVolumeRef.current = muted ? 0 : volume * crashCountdownGain
    if (!countdownStoppingRef.current) {
      crashCountdownSound.volume(countdownVolumeRef.current)
    }
  }, [volume, muted, crashCountdownGain])

  return null
}
